<template>
  <div class="animated fadeIn">

    <div class="card-widget">
      <div class="card-widget-title">
        <div class="card-widget-title-label">
          <h3>MDM Password</h3>
        </div>
        <div class="float-lg-right mt-3 mt-lg-0">
          <button
              class="btn btn-primary"
              @click="showCreatePasswordModal">
            {{ $t('Add password') }}
          </button>
        </div>
      </div>
      <div class="card-widget-content">

        <!-- start:: search & filters -->
        <div class="search-tool">
          <div class="row">
            <div class="col-md-4">
              <div class="input-group flex-nowrap">
                <b-form-input
                    v-model="search.password"
                    type="text"
                    id="earch.password"
                    name="earch.password"
                    class="form-control"
                    :placeholder="$t('Search password')">
                </b-form-input>
                <div class="input-group-append">
                  <span class="input-group-text" id="addon-wrapping"><i class="fas fa-search"></i></span>
                </div>
              </div>
            </div>
            <div class="col-md-8">
              <button type="button" id="buttonAdvancedFilters" class="btn btn-outline-primary" data-toggle="collapse"
                      href="#advancedFilters" role="button" aria-expanded="false" aria-controls="collapseExample"><i
                  class="fas fa-filter"></i> <span>{{ $t('Advanced Search') }}</span></button>
              <button type="button" id="buttonAdvancedFiltersReset" class="btn btn-outline-secondary" href="#"
                      role="button" @click="resetSearch"><i class="fas fa-redo"></i> <span>{{ $t('Reset') }}</span>
              </button>
            </div>
            <div class="col-md-12">
              <div class="collapse" id="advancedFilters">
                <div class="card card-body advanced-filters">
                  <form>
                    <div class="row">
                      <div class="col-md-4">
                        <b-form-group :label="$t('Expiration')" label-for="search.expirationState">
                          <multiselect
                              id="search.expirationState"
                              name="search.expirationState"
                              v-validate="'required'"
                              :searchable="false"
                              track-by="value"
                              :allowEmpty="false"
                              open-direction="bottom"
                              label="label"
                              v-model="search.expirationState"
                              :options="expirationStateSelectOptions"
                              :placeholder="$t('Select')"
                          ></multiselect>
                        </b-form-group>
                      </div>
                      <div class="col-md-4">

                        <b-form-group :label="$t('Search into notes')" label-for="search.notes">
                          <div class="input-group flex-nowrap">
                            <b-form-input
                                v-model="search.notes"
                                type="text"
                                id="search.notes"
                                name="search.notes"
                                class="form-control"
                                :placeholder="$t('Search')">
                            </b-form-input>
                            <div class="input-group-append">
                              <span class="input-group-text" id="addon-wrapping"><i class="fas fa-search"></i></span>
                            </div>
                          </div>
                        </b-form-group>

                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- end:: search & filters -->

        <b-table
            striped
            hover
            ref="table"
            :current-page="pagination.currentPage"
            :per-page="pagination.perPage"
            :show-empty="true"
            :empty-text="$t('There are no records to show')"
            :empty-filtered-text="$t('There are no records matching your request')"
            :filter="filterSearch"
            :filter-debounce="500"
            :fields="tableFields"
            :items="mdmPasswordProvider"
            primary-key="id"
            class="table table-responsive-sm"
        >
          <template v-slot:cell(password)="{ value, item }">
            {{ value }}
            <a class="ml-2" href="#" @click.prevent="copyPassword(value)">
              <icon-copy :width="15" :height="17"/>
            </a>
          </template>

          <template v-slot:cell(expire_at)="{ value, item }">
            {{ value ? dateFormatter(value) : $t("Long-lasting") }}
            <icon-expire-warning
                v-if="item.is_expired"
                class="align-top"
                :iconkey="'tooltip-update-' + item.id"
                :title="$t('Expired')"
            />
          </template>

          <template v-slot:cell(action)="row">
            <div class="action-button">
              <b-button
                  v-if="!row.item.status"
                  variant="btn btn-outline-danger ml-auto"
                  @click="deletePasswordModal.passwordToDelete = row.item; $bvModal.show('delete-password-modal');"
              >
                <i class="fas fa-trash"></i>
              </b-button>
            </div>
          </template>

          <template v-slot:table-busy>
            <div class="text-center text-muted my-2">
              <b-spinner class="align-middle"></b-spinner>
              <strong>{{ $t('Loading...') }}</strong>
            </div>
          </template>

        </b-table>

        <table-pagination :pagination="pagination" :isBusy="isBusy" :totalLabel="$t('Total items')"></table-pagination>
      </div>
    </div>

    <b-modal id="create-password-modal"
         :title="$t('Add password')"
         :ok-title="$t('Continue')"
         :cancel-title="$t('Dismiss')"
         cancel-variant="outline-secondary"
         @ok="createPasswordHandler" effect="fade/zoom"
         v-model="isCreatePasswordModalVisible"
          :ok-disabled="isCreatingMdmPassword"
          :cancel-disabled="isCreatingMdmPassword">
      <b-form-group :label="$t('Expiration')" label-for="expiration_period">
        <multiselect
            id="expiration_period"
            name="expiration_period"
            v-validate="'required'"
            :searchable="false"
            track-by="value"
            :allowEmpty="false"
            open-direction="bottom"
            label="label"
            v-model="createPasswordModal.expirationPeriod"
            :options="expirationPeriodSelectOptions"
            :placeholder="$t('Select')"
        ></multiselect>
      </b-form-group>
      <b-form-group :label="$t('Notes')" label-for="notes">
        <b-form-textarea
            id="notes"
            name="notes"
            :placeholder="$t('Notes')"
            rows="2"
            v-validate="'max:65'"
            maxlength="65"
            v-model='createPasswordModal.notes'></b-form-textarea>
      </b-form-group>
    </b-modal>

    <b-modal id="delete-password-modal"
             :title="$t('Delete')"
             :ok-title="$t('Continue')"
             :cancel-title="$t('Dismiss')"
             cancel-variant="outline-secondary"
             ok-variant="danger"
             @hidden="resetDeletePasswordModal"
             @ok="deletePasswordHandler" effect="fade/zoom">
      <p>
        {{
          $t('Do you want to delete the invitation? The dealer will no longer be able to complete the registration.')
        }}</p>
    </b-modal>

  </div>
</template>

<script>

import apiHelpers from "../../js/services/apiHelpers";
import {TablePagination} from "../../components";
import tableQuerySync from "../../js/services/tableQuerySync";
import IconOtherDealer from "../components/IconOtherDealer.vue";
import moment from "moment/moment";
import IconExpireWarning from "../../components/IconExpireWarning.vue";
import IconCopy from "../../components/IconCopy.vue";
import {copyToClipboard} from "../../js/services/copyToClipboard";

export default {
  name: 'DistributorAccounts',
  mixins: [apiHelpers, tableQuerySync],
  components: {
    IconCopy,
    IconExpireWarning,
    IconOtherDealer,
    TablePagination,
  },
  data() {
    return {
      tableFields: [
        {
          key: 'password',
          label: this.$t("Password"),
          sortable: false
        },
        {
          key: 'expire_at',
          label: this.$t("Expire date"),
          sortable: false
        },
        {
          key: 'notes',
          label: this.$t("Notes"),
          sortable: false
        },
        {
          key: 'action',
          label: '',
          sortable: false,
          thStyle: {minWidth: '160px'}
        }
      ],
      expirationPeriodSelectOptions: [
        {
          value: 1,
          label: '1 ' + this.$t('day')
        },
        {
          value: 2,
          label: '1 ' + this.$t('week')
        },
        {
          value: 3,
          label: '1 ' + this.$t('month')
        },
        {
          value: 4,
          label: this.$t('Long-lasting')
        },
      ],
      expirationStateSelectOptions: [
        {
          value: 1,
          label: this.$t('Expired')
        },
        {
          value: 2,
          label: this.$t('Active')
        },
        {
          value: 3,
          label: this.$t('Long-lasting')
        },
      ],
      createPasswordModal: {
        expirationPeriod: null,
        notes: null,
      },
      isCreatePasswordModalVisible: false,
      isCreatingMdmPassword: false,
      pagination: {
        perPage: 10,
      },
      search: {
        password: '',
        expirationState: '',
        notes: ''
      },
      inviteID: null,
      accounts: [],
      deletePasswordModal: {
        passwordToDelete: null,
      },
    }
  },
  mounted() {
    this.resetCreatePasswordModal();
  },
  methods: {
    resetSearch() {
      this.search = {
        password: '',
        expirationState: '',
        notes: ''
      }

      this.pagination.currentPage = 1;
    },
    mdmPasswordProvider(ctx) {
      const url = `${this.$store.getters.getAppUrl}/dealer/api/v2/mdm-passwords`;
      return axios.get(url, {params: this.preparePaginationQuery(ctx)}).then(({data}) => {
        this.updatePaginationMeta(data);
        return data.data;
      }).catch(e => {
        console.log(e)
      });
    },
    dateToMoment(date) {
      if (moment.isMoment(date)) {
        return date;
      }

      return moment(date);
    },
    dateFormatter(date) {
      return this.dateToMoment(date).format("DD-MM-YYYY");
    },
    showCreatePasswordModal() {
      this.isCreatePasswordModalVisible = true;
    },
    hideCreatePasswordModal() {
      this.isCreatePasswordModalVisible = false;
    },
    async createPasswordHandler(e) {
      e.preventDefault();
      const result = await this.$validator.validate('notes');

      if (!result) {
        return;
      }

      this.isCreatingMdmPassword = true;

      const url = `${this.$store.getters.getAppUrl}/dealer/api/v2/mdm-passwords`;
      const data = {
        expiration_period: this.createPasswordModal.expirationPeriod?.value,
        notes: this.createPasswordModal.notes
      }
      axios.post(url, data)
          .then((response) => {
            if (response) {
              this.$notify({
                group: 'form',
                type: 'success',
                title: this.$t('Success'),
                text: this.$t('Saved'),
              });

              this.hideCreatePasswordModal();
              this.resetCreatePasswordModal();
              this.$refs.table.refresh();
            }
          })
          .catch(function (e) {
            console.log(e)
          }).finally(() => {
        this.isCreatingMdmPassword = false;
      });
    },
    deletePasswordHandler() {
      const url = `${this.$store.getters.getAppUrl}/dealer/api/v2/mdm-passwords/${this.deletePasswordModal.passwordToDelete.id}`;

      axios.delete(url)
          .then((response) => {
            if (response) {
              this.$notify({
                group: 'form',
                type: 'success',
                title: this.$t('Success'),
                text: this.$t('Deleted'),
              });
              this.resetDeletePasswordModal();
              this.$refs.table.refresh();
            }
          })
          .catch(function (error) {
          });
    },
    resetDeletePasswordModal() {
      this.deletePasswordModal.passwordToDelete = null;
      this.deletePasswordModal.notes = null;
    },
    resetCreatePasswordModal() {
      this.createPasswordModal.expirationPeriod = this.expirationPeriodSelectOptions[0];
      this.createPasswordModal.notes = null;
    },
    validationState: function (field) {
      if (
          this.veeFields[field] &&
          (this.veeFields[field].dirty || this.veeFields[field].validated)
      ) {
        return !this.veeErrors.has(field)
      }
      return null
    },
    copyPassword(textToCopy) {
      if (copyToClipboard(textToCopy)) {
        this.copyNotify('info')
      } else {
        this.copyNotify('error')
      }
    },
    copyNotify(type) {
      this.$notify({
        type: type,
        text: (type === 'info') ? this.$t('Password copied to clipboard.') : this.$t('An error occurred while copying the code. Try again.'),
      })
    },
  },
  computed: {
    filterSearch() {
      const params = {};

      if (this.search.password !== '') {
        params['filter[password]'] = this.search.password;
      }

      if (this.search.expirationState?.value) {
        params['filter[expiration_state]'] = this.search.expirationState.value;
      }

      if (this.search.notes !== '') {
        params['filter[notes]'] = this.search.notes;
      }

      return params;
    },
  },
}
</script>
